import React, { useState, useEffect, useCallback, useRef } from 'react';
import ConfirmationModal from '../../common/ConfirmationModal';
import {
  registrationAPI,
  StandardiseResult,
  UniquenessResult,
  RegisteredCompound,
  TherapeuticArea,
} from '../../../services/registrationApi';
import { formatDate } from '../../../utils/dateFormat';

// =====================================================================
// Props
// =====================================================================

interface RegistrationTabProps {
  smiles: string;
  molfile?: string;
}

// =====================================================================
// Status badge (matches RegistryPage palette)
// =====================================================================

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  registered:  { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  candidate:   { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  preclinical: { bg: '#fefce8', text: '#854d0e', border: '#fef08a' },
  development: { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  clinical:    { bg: '#faf5ff', text: '#7c3aed', border: '#ddd6fe' },
  approved:    { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  suspended:   { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  withdrawn:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' },
  archived:    { bg: '#f8fafc', text: '#64748b', border: '#e2e8f0' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.registered;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
    >
      {status}
    </span>
  );
};

// =====================================================================
// Copy button
// =====================================================================

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-1 p-0.5 rounded transition-colors"
      style={{ color: copied ? '#0D9488' : 'var(--text-muted)' }}
      title="Copy to clipboard"
    >
      {copied ? (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  );
};

// =====================================================================
// Property row helper
// =====================================================================

const PropRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span className={`text-xs ${mono ? 'font-mono' : ''}`} style={{ color: 'var(--text-primary)' }}>{value}</span>
  </div>
);

// =====================================================================
// Main component
// =====================================================================

type Phase = 'loading' | 'error' | 'already_registered' | 'new_compound' | 'existing_parent' | 'registering' | 'registered';

const RegistrationTab: React.FC<RegistrationTabProps> = ({ smiles, molfile }) => {
  // Pipeline state
  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Standardisation + uniqueness results
  const [stdResult, setStdResult] = useState<StandardiseResult | null>(null);
  const [uniqueness, setUniqueness] = useState<UniquenessResult | null>(null);

  // Existing compound detail (when already registered)
  const [existing, setExisting] = useState<RegisteredCompound | null>(null);

  // Confirmation modal state (handler defined after runPipeline)
  const [confirmAction, setConfirmAction] = useState<{
    type: 'withdraw' | 'reinstate' | 'promote' | 'demote';
    code: string;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Registration form state
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const [selectedTA, setSelectedTA] = useState<string | undefined>(undefined);
  const [notes, setNotes] = useState('');

  // Result after successful registration
  const [regResult, setRegResult] = useState<{ parent_code: string; version_code: string; lot_code: string; message: string } | null>(null);

  // Guard against race conditions: each pipeline run gets a unique id
  const pipelineIdRef = useRef(0);

  // Auto-run pipeline on mount / SMILES change
  const runPipeline = useCallback(async () => {
    const runId = ++pipelineIdRef.current;

    setPhase('loading');
    setErrorMsg(null);
    setStdResult(null);
    setUniqueness(null);
    setExisting(null);
    setRegResult(null);

    try {
      // Step 1: Standardise — prefer molfile (preserves tautomer) over SMILES (canonical)
      const trimmedMolfile = molfile?.trim();
      const trimmedSmiles = smiles?.trim();
      if (!trimmedMolfile && !trimmedSmiles) {
        setErrorMsg('No structure available — compound has no SMILES or molblock');
        setPhase('error');
        return;
      }
      let stdR;
      if (trimmedMolfile) {
        try {
          stdR = await registrationAPI.standardise({ molblock: trimmedMolfile });
        } catch {
          if (pipelineIdRef.current !== runId) return; // superseded
          // MOL block unparseable — fall back to SMILES
          if (trimmedSmiles) {
            stdR = await registrationAPI.standardise({ smiles: trimmedSmiles });
          } else {
            throw new Error('Could not parse structure');
          }
        }
      } else {
        stdR = await registrationAPI.standardise({ smiles: trimmedSmiles! });
      }
      if (pipelineIdRef.current !== runId) return; // superseded
      const std = stdR.data;
      setStdResult(std);

      // Step 2: Check uniqueness (send canonical SMILES — backend re-standardises)
      const uniR = await registrationAPI.checkUniqueness({ smiles: std.canonical_smiles });
      if (pipelineIdRef.current !== runId) return; // superseded
      const uni = uniR.data;
      setUniqueness(uni);

      if (!uni.is_unique) {
        // Fetch full compound detail
        if (uni.matched_parent_code) {
          try {
            const compR = await registrationAPI.getCompound(uni.matched_parent_code);
            if (pipelineIdRef.current !== runId) return;
            setExisting(compR.data);
          } catch {
            // Non-critical — we still show the banner
          }
        }
        setPhase('already_registered');
      } else {
        setPhase('new_compound');
      }
    } catch (err: any) {
      if (pipelineIdRef.current !== runId) return; // superseded
      setErrorMsg(err.response?.data?.detail || err.message || 'Registration check failed');
      setPhase('error');
    }
  }, [smiles, molfile]);

  useEffect(() => {
    if (smiles?.trim() || molfile?.trim()) runPipeline();
    else {
      setErrorMsg('No structure available — compound has no SMILES or molblock');
      setPhase('error');
    }
  }, [runPipeline, smiles, molfile]);

  // Fetch therapeutic areas for the form
  useEffect(() => {
    registrationAPI.getTherapeuticAreas()
      .then(r => setTherapeuticAreas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Handle registration
  const handleRegister = async () => {
    setPhase('registering');
    setErrorMsg(null);
    try {
      const r = await registrationAPI.register({
        smiles: stdResult?.canonical_smiles || smiles || undefined,
        therapeutic_area: selectedTA || undefined,
        notes: notes || undefined,
      });
      setRegResult(r.data);
      setPhase('registered');
    } catch (err: any) {
      setErrorMsg(err.response?.data?.detail || 'Registration failed');
      setPhase('error');
    }
  };

  // Confirmation modal handler + config
  const CONFIRM_CFG: Record<string, { title: (c: string) => string; desc: string; label: string; color: 'red' | 'green' | 'blue' | 'amber' }> = {
    withdraw:  { title: (c) => `Withdraw ${c}?`, desc: 'This will soft-delete the compound.', label: 'Withdraw', color: 'red' },
    reinstate: { title: (c) => `Reinstate ${c}?`, desc: 'Restore to active status.', label: 'Reinstate', color: 'green' },
    promote:   { title: (c) => `Promote ${c}?`, desc: 'Promote to development status.', label: 'Promote', color: 'blue' },
    demote:    { title: (c) => `Demote ${c}?`, desc: 'Revert to registered status.', label: 'Demote', color: 'amber' },
  };

  const handleConfirmAction = useCallback(async (reason?: string) => {
    if (!confirmAction || !reason?.trim()) return;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const { type, code } = confirmAction;
      if (type === 'withdraw')  await registrationAPI.withdraw(code, reason.trim());
      if (type === 'reinstate') await registrationAPI.reinstate(code, reason.trim());
      if (type === 'promote')   await registrationAPI.promote(code, reason.trim());
      if (type === 'demote')    await registrationAPI.demote(code, reason.trim());
      setConfirmAction(null);
      runPipeline();
    } catch (err: any) {
      setConfirmError(err.response?.data?.detail || `Failed to ${confirmAction.type}`);
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmAction, runPipeline]);

  // ===================================================================
  // LOADING
  // ===================================================================
  if (phase === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-6 h-6 border-2 border-t-teal-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Checking registration status...</span>
      </div>
    );
  }

  // ===================================================================
  // ERROR
  // ===================================================================
  if (phase === 'error') {
    return (
      <div className="space-y-3">
        <div className="rounded-lg px-3 py-2.5 text-xs" style={{ backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
          {errorMsg}
        </div>
        <button
          onClick={runPipeline}
          className="w-full px-3 py-2 text-xs font-medium rounded-lg border transition-colors"
          style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ===================================================================
  // STATE A: Already Registered (exact duplicate)
  // ===================================================================
  if (phase === 'already_registered') {
    return (
      <div className="space-y-3">
        {/* Green banner */}
        <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium" style={{ color: '#166534' }}>
            Registered as{' '}
            <a
              href="/chemistry/registry"
              className="font-mono underline"
              style={{ color: '#0D9488' }}
            >
              {uniqueness?.matched_parent_code}
            </a>
          </span>
        </div>

        {/* Compound info */}
        {existing && (
          <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono font-medium" style={{ color: '#0D9488' }}>{existing.parent_code}</span>
              <StatusBadge status={existing.status} />
            </div>
            {existing.therapeutic_area_name && (
              <PropRow label="Therapeutic Area" value={existing.therapeutic_area_name} />
            )}
            {existing.notes && (
              <PropRow label="Notes" value={existing.notes} />
            )}
            <PropRow label="Registered" value={formatDate(existing.created_at)} />

            {/* Versions */}
            {existing.versions && existing.versions.length > 0 && (
              <div className="pt-2 border-t space-y-1.5" style={{ borderColor: 'var(--card-border)' }}>
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                  Versions ({existing.versions.length})
                </span>
                {existing.versions.map(v => (
                  <div key={v.version_code} className="rounded border px-2.5 py-1.5" style={{ borderColor: 'var(--card-border)' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{v.version_code}</span>
                      {v.salt_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                          {v.salt_name}
                        </span>
                      )}
                      {v.is_candidate && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          Candidate
                        </span>
                      )}
                    </div>
                    {v.lots.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {v.lots.map(lot => (
                          <div key={lot.lot_code} className="flex items-center gap-2 text-[10px] pl-2 border-l" style={{ borderColor: '#0D9488', color: 'var(--text-secondary)' }}>
                            <span className="font-mono">{lot.lot_code}</span>
                            {lot.purity_percent != null && <span>Purity: {lot.purity_percent}%</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* INN / Clinical info */}
        {existing && ((existing as any).inn_name || (existing as any).nct_id) && (
          <div className="rounded-lg border px-3 py-2 space-y-1" style={{ borderColor: '#bfdbfe', backgroundColor: '#eff6ff' }}>
            {(existing as any).inn_name && (
              <div>
                <span className="text-[10px] font-medium" style={{ color: '#1e40af' }}>INN: </span>
                <span className="text-xs italic" style={{ color: '#1e40af' }}>{(existing as any).inn_name}</span>
                {(existing as any).inn_stem && (
                  <span className="text-[10px] ml-1" style={{ color: '#3b82f6' }}>({(existing as any).inn_stem})</span>
                )}
              </div>
            )}
            {(existing as any).nct_id && (
              <div>
                <span className="text-[10px] font-medium" style={{ color: '#7c3aed' }}>Clinical: </span>
                <a
                  href={`https://clinicaltrials.gov/study/${(existing as any).nct_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline"
                  style={{ color: '#7c3aed' }}
                >{(existing as any).nct_id}</a>
                {(existing as any).clinical_phase && (
                  <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
                    {(existing as any).clinical_phase.replace('_', ' ')}
                  </span>
                )}
              </div>
            )}
            {(existing as any).promotion_reason && (
              <div className="text-[10px]" style={{ color: '#3b82f6' }}>{(existing as any).promotion_reason}</div>
            )}
          </div>
        )}

        {/* Actions based on status */}
        {existing && existing.status === 'development' && (
          <div className="space-y-1.5">
            <button
              onClick={() => setConfirmAction({ type: 'demote', code: existing.parent_code })}
              className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors hover:bg-amber-50"
              style={{ borderColor: '#fde68a', color: '#92400e' }}
            >
              Demote to Registered
            </button>
          </div>
        )}
        {existing && existing.status !== 'withdrawn' && existing.status !== 'development' && (
          <>
            <button
              onClick={() => setConfirmAction({ type: 'promote', code: existing.parent_code })}
              className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors hover:bg-blue-50"
              style={{ borderColor: '#bfdbfe', color: '#1e40af' }}
            >
              Promote to Development
            </button>
            <button
              onClick={() => setConfirmAction({ type: 'withdraw', code: existing.parent_code })}
              className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors hover:bg-red-50"
              style={{ borderColor: '#fecaca', color: '#991b1b' }}
            >
              Withdraw
            </button>
          </>
        )}
        {existing && existing.status === 'withdrawn' && (
          <button
            onClick={() => setConfirmAction({ type: 'reinstate', code: existing.parent_code })}
            className="w-full px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors hover:bg-emerald-50"
            style={{ borderColor: '#a7f3d0', color: '#065f46' }}
          >
            Reinstate
          </button>
        )}

        {/* Standardisation details */}
        {stdResult && <StdDetails std={stdResult} />}

        {/* Confirmation modal */}
        {confirmAction && (
          <ConfirmationModal
            isOpen={!!confirmAction}
            onClose={() => { setConfirmAction(null); setConfirmError(null); }}
            onConfirm={handleConfirmAction}
            title={CONFIRM_CFG[confirmAction.type].title(confirmAction.code)}
            description={CONFIRM_CFG[confirmAction.type].desc}
            confirmLabel={CONFIRM_CFG[confirmAction.type].label}
            confirmColor={CONFIRM_CFG[confirmAction.type].color}
            requireReason
            loading={confirmLoading}
            error={confirmError}
          />
        )}
      </div>
    );
  }

  // ===================================================================
  // STATE C: Existing Parent — new version will be created
  // ===================================================================
  if (phase === 'existing_parent') {
    return (
      <div className="space-y-3">
        {/* Blue banner */}
        <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#2563eb' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium" style={{ color: '#1e40af' }}>
            Parent exists as{' '}
            <a href="/chemistry/registry" className="font-mono underline" style={{ color: '#0D9488' }}>
              {uniqueness?.matched_parent_code}
            </a>
            {' '}&mdash; registering creates a new version
          </span>
        </div>

        {stdResult && <StdDetails std={stdResult} />}
        <RegistrationForm
          therapeuticAreas={therapeuticAreas}
          selectedTA={selectedTA}
          setSelectedTA={setSelectedTA}
          notes={notes}
          setNotes={setNotes}
          onRegister={handleRegister}
          registering={false}
          label="Register New Version"
        />
      </div>
    );
  }

  // ===================================================================
  // STATE B: New Compound
  // ===================================================================
  if (phase === 'new_compound') {
    return (
      <div className="space-y-3">
        {/* Blue banner */}
        <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#2563eb' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-xs font-medium" style={{ color: '#1e40af' }}>
            New compound &mdash; ready to register
          </span>
        </div>

        {stdResult && <StdDetails std={stdResult} />}
        <RegistrationForm
          therapeuticAreas={therapeuticAreas}
          selectedTA={selectedTA}
          setSelectedTA={setSelectedTA}
          notes={notes}
          setNotes={setNotes}
          onRegister={handleRegister}
          registering={false}
          label="Register Compound"
        />
      </div>
    );
  }

  // ===================================================================
  // REGISTERING
  // ===================================================================
  if (phase === 'registering') {
    return (
      <div className="space-y-3">
        <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
          <span className="text-xs font-medium" style={{ color: '#1e40af' }}>Registering...</span>
        </div>
        {stdResult && <StdDetails std={stdResult} />}
      </div>
    );
  }

  // ===================================================================
  // REGISTERED — success
  // ===================================================================
  if (phase === 'registered' && regResult) {
    return (
      <div className="space-y-3">
        {/* Green success banner */}
        <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium" style={{ color: '#166534' }}>Registered successfully</span>
        </div>

        <div className="rounded-lg border p-3 space-y-1.5" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Parent</span>
            <a href="/chemistry/registry" className="text-xs font-mono font-medium underline" style={{ color: '#0D9488' }}>
              {regResult.parent_code}
            </a>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Version</span>
            <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{regResult.version_code}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Lot</span>
            <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{regResult.lot_code}</span>
          </div>
        </div>

        {stdResult && <StdDetails std={stdResult} />}
      </div>
    );
  }

  return null;
};

// =====================================================================
// Standardisation details card
// =====================================================================

const StdDetails: React.FC<{ std: StandardiseResult }> = ({ std }) => (
  <div className="rounded-lg border p-3 space-y-1" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
    <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
      Standardisation
    </span>
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Canonical SMILES</span>
      <div className="flex items-center max-w-[200px]">
        <span className="text-xs font-mono truncate" title={std.canonical_smiles} style={{ color: 'var(--text-primary)' }}>
          {std.canonical_smiles}
        </span>
        <CopyButton text={std.canonical_smiles} />
      </div>
    </div>
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>InChIKey</span>
      <div className="flex items-center">
        <span className="text-[10px] font-mono" style={{ color: 'var(--text-primary)' }}>{std.inchi_key}</span>
        <CopyButton text={std.inchi_key} />
      </div>
    </div>
    <PropRow label="Formula" value={std.mol_formula} mono />
    <PropRow label="MW" value={Math.round(std.mol_weight * 100) / 100} mono />
    <PropRow label="Exact Mass" value={Math.round(std.exact_mass * 10000) / 10000} mono />
    <PropRow
      label="Stereocenters"
      value={`${std.num_defined_stereocenters} def / ${std.num_stereocenters - std.num_defined_stereocenters} undef`}
      mono
    />
    {std.salts_removed && std.salts_removed.length > 0 && (
      <div className="pt-1 border-t" style={{ borderColor: 'var(--card-border)' }}>
        <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>Salts removed:</span>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {std.salts_removed.map((s, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
              {s.name}
            </span>
          ))}
        </div>
      </div>
    )}
    {std.warnings && std.warnings.length > 0 && (
      <div className="pt-1 border-t" style={{ borderColor: 'var(--card-border)' }}>
        {std.warnings.map((w, i) => (
          <div key={i} className="text-[10px] py-0.5" style={{ color: '#d97706' }}>{w}</div>
        ))}
      </div>
    )}
  </div>
);

// =====================================================================
// Registration form (shared by new compound + existing parent states)
// =====================================================================

const RegistrationForm: React.FC<{
  therapeuticAreas: TherapeuticArea[];
  selectedTA: string | undefined;
  setSelectedTA: (v: string | undefined) => void;
  notes: string;
  setNotes: (v: string) => void;
  onRegister: () => void;
  registering: boolean;
  label: string;
}> = ({ therapeuticAreas, selectedTA, setSelectedTA, notes, setNotes, onRegister, registering, label }) => (
  <div className="rounded-lg border p-3 space-y-3" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
    <div>
      <label className="block text-[10px] font-medium mb-1 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        Therapeutic Area
      </label>
      <select
        value={selectedTA ?? ''}
        onChange={(e) => setSelectedTA(e.target.value || undefined)}
        className="w-full px-2.5 py-1.5 text-xs rounded-lg border transition-colors"
        style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
      >
        <option value="">-- Select --</option>
        {therapeuticAreas.map(ta => (
          <option key={ta.code} value={ta.code}>{ta.code} &mdash; {ta.name}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block text-[10px] font-medium mb-1 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        Notes
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full px-2.5 py-1.5 text-xs rounded-lg border transition-colors resize-none"
        style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
        placeholder="Optional notes..."
      />
    </div>
    <button
      onClick={onRegister}
      disabled={registering}
      className="w-full px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-50"
      style={{ backgroundColor: '#0D9488' }}
    >
      {registering ? 'Registering...' : label}
    </button>
  </div>
);

export default RegistrationTab;
